import { Skeleton, SkeletonAvatar, SkeletonRegion, SkeletonTable, DayStatStrip } from '@parsina/framework';
import { C, CT, RADIUS, SHADOW } from '@parsina/framework/tokens';
import { STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import MobileScreen from '../../mobile/MobileScreen';
import MobileHeader from '../../mobile/MobileHeader';
import MobileList from '../../mobile/MobileList';
import MobileCardSkeleton from '../../mobile/MobileCardSkeleton';

// The loading shape of پذیرش — DESIGN.md §"Loading & skeleton".
//
// This file is the router's Suspense fallback for the lazy Reception chunk AND
// the screen's own first-load state, so it must stay light: nothing here
// imports the screen, its hooks or its vocabulary. The geometry is copied from
// the real components by hand (Reception.web.jsx's KpiBar / DoctorTabs /
// FilterBar, DayList's table, MobileAppointmentCard) — change one of those and change its block here in
// the same commit, so the data lands in the box the reader is already looking
// at rather than shoving it.

// ── Desktop ──────────────────────────────────────────────────────────────────

// KPI tiles at the day panel's own geometry: 32px icon box, radius 13, padding
// 11×13. The بدهی tile is today-only, so the row is one tile shorter on any
// other day — the real KpiBar does the same.
export const KPI_COLS = 6;

// The blocks alone (aria-hidden) — a page skeleton nests them under its own
// status root; the screen's KPI-region refetch uses the labelled composite.
function KpiTiles({ isToday, ...rest }) {
  const n = isToday ? KPI_COLS : KPI_COLS - 1;
  return (
    <div
      aria-hidden
      style={{ display: 'grid', gridTemplateColumns: `repeat(${n},1fr)`, gap: 9, padding: '6px 24px 14px' }}
      {...rest}
    >
      {Array.from({ length: n }, (_, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: CT.surface, border: `1px solid ${CT.border}`,
          borderRadius: 13, padding: '11px 13px',
        }}>
          <Skeleton width={32} height={32} radius={9} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Skeleton width="60%" height={18} radius={4} />
            <Skeleton width="80%" height={11} radius={4} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReceptionKpiSkeleton({ isToday = true, ...rest }) {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={t('common.state.loading')} {...rest}>
      <KpiTiles isToday={isToday} />
    </SkeletonRegion>
  );
}

// The day table's real columns: #, drag handle, time, patient, status, money,
// actions. DayList rows are ROW_PAD.compact (8px) around a 34px avatar = 50px;
// the kit's 52px row is the nearest step.
const DAY_COLUMNS = [24, 24, 60, 200, 96, 96, 140];

function DoctorTabsSkeleton() {
  return (
    <div aria-hidden style={{ display: 'flex', gap: 7, padding: '0 24px 12px', flexWrap: 'wrap', alignItems: 'center' }}>
      {[112, 150, 150].map((w, i) => <Skeleton key={i} width={w} height={45} radius={RADIUS.pill} />)}
    </div>
  );
}

// Reception.shared's FILTERS has five chips: 7px 13px padding on 12.5px text.
const FILTER_CHIPS = [78, 68, 104, 84, 74];

function FilterChipsSkeleton() {
  return (
    <div aria-hidden style={{ display: 'flex', gap: 8, padding: '0 24px 12px', flexWrap: 'wrap' }}>
      {FILTER_CHIPS.map((w, i) => <Skeleton key={i} width={w} height={32} radius={RADIUS.pill} />)}
    </div>
  );
}

export function ReceptionSkeletonWeb({ isToday = true }) {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={t('common.state.loading')}
      data-testid="reception-skeleton"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: CT.bg }}>
      {/* The command bar: title (real — it needs no data), view switch, the day
          navigator, the search box that takes the slack, and «ثبت نوبت». */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 24px 12px', flexWrap: 'wrap', background: CT.surface, borderBottom: `1px solid ${CT.border}`,
      }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: CT.ink, whiteSpace: 'nowrap' }}>{t('nav.items.reception')}</h1>
        <div style={{ width: 1, alignSelf: 'stretch', background: CT.border, margin: '2px 0' }} />
        {/* Three segments: «فهرست روز»، «درخواست‌های نوبت»، «اقدام‌های درمانی». */}
        <Skeleton width={300} height={32} radius={10} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <Skeleton width={30} height={30} radius={8} />
          <Skeleton width={190} height={30} radius={8} />
          <Skeleton width={30} height={30} radius={8} />
          <Skeleton width={56} height={30} radius={8} />
        </div>
        {/* Search + «ثبت نوبت» wrap as one group, as on the real bar. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '1 1 410px', marginInlineStart: 'auto', minWidth: 0 }}>
          <div style={{ flex: 1, minWidth: 260, maxWidth: 560, marginInlineStart: 'auto' }}>
            <Skeleton width="100%" height={38} radius={10} />
          </div>
          <Skeleton width={150} height={36} radius={10} />
        </div>
      </header>

      <KpiTiles isToday={isToday} />
      <DoctorTabsSkeleton />
      <FilterChipsSkeleton />
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '4px 24px 32px' }}>
        <SkeletonTable rows={8} columns={DAY_COLUMNS} label={t('common.state.loading')} style={{ borderRadius: 14 }} />
      </div>
    </SkeletonRegion>
  );
}

// ── Mobile ───────────────────────────────────────────────────────────────────

// One MobileAppointmentCard in placeholder: ① time · flow status, ② 34px avatar
// · name · pay chip, ③ meta line, ④ the primary action and ⋮. The box is
// MobileCard's own (padding, radius, border, shadow).
export function ReceptionAppointmentCardSkeleton({ style, ...rest }) {
  return (
    <div
      aria-hidden
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIUS.card,
        boxShadow: SHADOW.card, padding: '0.75rem 0.875rem', minHeight: '2.75rem', ...style,
      }}
      {...rest}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Skeleton width={44} height={15} />
        <span style={{ flex: 1 }} />
        <Skeleton width={56} height={12} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
        <SkeletonAvatar size={34} />
        <Skeleton width="48%" height={14} style={{ flex: 1, maxWidth: '48%' }} />
        <span style={{ flex: 1 }} />
        <Skeleton width={72} height={22} radius={RADIUS.pill} />
      </div>
      <Skeleton width="62%" height={11} style={{ marginTop: '0.25rem' }} />
      <div style={{
        display: 'flex', gap: `${STACK.tight}px`, alignItems: 'center',
        marginTop: '0.625rem', paddingTop: '0.625rem', borderTop: `1px solid ${C.border}`,
      }}>
        <Skeleton height={44} radius={RADIUS.control} style={{ flex: 1, width: 'auto' }} />
        <Skeleton width={44} height={44} radius={RADIUS.control} />
      </div>
    </div>
  );
}

// The registry hit card (clinic-wide search on the phone): name · chip, one
// meta line, «نوبت» + ⋮.
export function ReceptionRegistryCardSkeleton(props) {
  return <MobileCardSkeleton avatar={0} metaLines={1} badge={0} actions={2} {...props} />;
}

export function ReceptionSkeletonMobile() {
  const { t } = useTranslation();
  // The day cockpit's geometry (PM-0052): the header card (lens row 44 + day
  // bar 48), the one-row strip — the kit's own, loading, so its «–» figures sit
  // exactly where the counts land — then «ثبت نوبت» and the cards.
  const strip = [
    { key: 'all', label: t('reception.mobile.strip.total') },
    { key: 'present', label: t('reception.status.present') },
    { key: 'visited', label: t('reception.status.seen') },
    { key: 'noshow', label: t('reception.status.noShow') },
  ].map((x) => ({ ...x, value: null }));

  const header = (
    <>
      <MobileHeader>
        <Skeleton tone="onDark" width="100%" height={92} radius={12} />
      </MobileHeader>
      <DayStatStrip items={strip} loading testIdPrefix="reception-skeleton" />
    </>
  );

  return (
    <SkeletonRegion label={t('common.state.loading')} data-testid="reception-skeleton" style={{ height: '100%' }}>
      <MobileScreen header={header} data-state="loading">
        <div style={{ padding: '0.75rem 1rem 0' }}>
          <Skeleton height={44} radius={RADIUS.control} style={{ width: '100%' }} />
        </div>
        <div style={{ padding: '0 1rem' }}>
          <MobileList
            loading
            items={[]}
            skeletonCount={4}
            renderSkeleton={(i) => <ReceptionAppointmentCardSkeleton key={i} />}
          />
        </div>
      </MobileScreen>
    </SkeletonRegion>
  );
}

export default function ReceptionSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <ReceptionSkeletonMobile {...props} /> : <ReceptionSkeletonWeb {...props} />;
}
